import React, { useState, useEffect } from 'react';
import './Dashboard.css';
import { fetchBookings, fetchPayments } from '../api';

const Dashboard = () => {
  const [dashboardData, setDashboardData] = useState({
    appointmentsToday: 0,
    totalPatients: 0,
    monthlyRevenue: 0,
    weeklyAppointments: [0, 0, 0, 0, 0, 0, 0]
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      
      // Fetch appointments for today
      const today = new Date().toISOString().split('T')[0];
      const appointmentsToday = await fetchBookings({
        date: today
      });
      const todayCount = appointmentsToday?.length || 0;

      // Fetch weekly appointments data and calculate unique patients from it
      const { weeklyData, allWeekBookings } = await fetchWeeklyAppointments();
      
      // Calculate unique patients from the weekly bookings data
      const uniquePatients = new Set();
      allWeekBookings.forEach(booking => {
        if (booking.paciente) {
          uniquePatients.add(booking.paciente);
        }
      });
      const totalPatients = uniquePatients.size;

      // Fetch monthly revenue (current month)
      const currentMonth = new Date().getMonth() + 1;
      const currentYear = new Date().getFullYear();
      const firstDayOfMonth = `${currentYear}-${String(currentMonth).padStart(2, '0')}-01`;
      const lastDayOfMonth = new Date(currentYear, currentMonth, 0).toISOString().split('T')[0];
      
      const paymentsResponse = await fetchPayments({
        start_date: firstDayOfMonth,
        end_date: lastDayOfMonth,
        status: 'paid'
      });
      const monthlyRevenue = paymentsResponse?.reduce((total, payment) => {
        return total + (parseFloat(payment.amount) || 0);
      }, 0) || 0;

      setDashboardData({
        appointmentsToday: todayCount,
        totalPatients,
        monthlyRevenue,
        weeklyAppointments: weeklyData
      });
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      // Set fallback data on error
      setDashboardData({
        appointmentsToday: 0,
        totalPatients: 0,
        monthlyRevenue: 0,
        weeklyAppointments: [12, 18, 25, 32, 28, 20, 15]
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchWeeklyAppointments = async () => {
    try {
      const weekData = [];
      const allBookings = [];
      const today = new Date();
      
      // Fetch bookings for each day of the week (past 7 days)
      for (let i = 6; i >= 0; i--) {
        const date = new Date(today);
        date.setDate(date.getDate() - i);
        const dateStr = date.toISOString().split('T')[0];
        
        try {
          const dayBookings = await fetchBookings({ date: dateStr });
          weekData.push(dayBookings?.length || 0);
          if (dayBookings) {
            allBookings.push(...dayBookings);
          }
        } catch (dayError) {
          console.error(`Error fetching bookings for ${dateStr}:`, dayError);
          weekData.push(0); // Add 0 for days with errors
        }
      }
      
      return { weeklyData: weekData, allWeekBookings: allBookings };
    } catch (error) {
      console.error('Error fetching weekly data:', error);
      return { 
        weeklyData: [12, 18, 25, 32, 28, 20, 15], // Fallback data
        allWeekBookings: []
      };
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const getMaxAppointments = () => {
    return Math.max(...dashboardData.weeklyAppointments, 1);
  };

  const getWeekDayLabels = () => {
    const labels = [];
    const today = new Date();
    
    for (let i = 6; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(date.getDate() - i);
      
      // Get short weekday name (Dom, Seg, Ter, etc.)
      const weekday = date.toLocaleDateString('pt-BR', { weekday: 'short' });
      labels.push(weekday);
    }
    
    return labels;
  };

  if (loading) {
    return (
      <div className="dashboard-loading">
        <div className="spinner"></div>
        <span>Carregando dashboard...</span>
      </div>
    );
  }

  return (
    <div className="dashboard">
      <div className="dashboard-container">
        <div className="dashboard-header">
          <div className="dashboard-title">
            <div className="nexus-logo">N</div>
            <span>Dashboard Clínica</span>
          </div>
          <div className="dashboard-actions">
            <button 
              className="refresh-button" 
              onClick={fetchDashboardData}
              disabled={loading}
              title="Atualizar dados"
            >
              🔄
            </button>
            <div className="dashboard-user">
              <div className="user-avatar">👨‍⚕️</div>
              <span>Nexus ERP</span>
            </div>
          </div>
        </div>
        
        <div className="dashboard-content">
          <div className="dashboard-cards">
            <div className="dash-card">
              <div className="card-icon">📅</div>
              <div className="card-info">
                <span className="card-number">{dashboardData.appointmentsToday}</span>
                <span className="card-label">Consultas Hoje</span>
              </div>
            </div>
            
            <div className="dash-card">
              <div className="card-icon">👥</div>
              <div className="card-info">
                <span className="card-number">{dashboardData.totalPatients.toLocaleString('pt-BR')}</span>
                <span className="card-label">Pacientes</span>
              </div>
            </div>
            
            <div className="dash-card">
              <div className="card-icon">💰</div>
              <div className="card-info">
                <span className="card-number">{formatCurrency(dashboardData.monthlyRevenue)}</span>
                <span className="card-label">Receita Mensal</span>
              </div>
            </div>
          </div>
          
          <div className="dashboard-chart">
            <div className="chart-title">📊 Atendimentos da Semana</div>
            <div className="chart-bars">
              {dashboardData.weeklyAppointments.map((appointments, index) => (
                <div key={index} className="chart-bar-container">
                  <div 
                    className="chart-bar" 
                    style={{
                      height: `${(appointments / getMaxAppointments()) * 100}%`
                    }}
                  ></div>
                  <span className="chart-label">{appointments}</span>
                  <span className="chart-day-label">{getWeekDayLabels()[index]}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard; 